import Phaser from 'phaser';
import {getPreferences} from './preferences';
import {renderPixelRatio} from './renderResolutionPolicy';

/**
 * Keep Phaser cameras, pointer coordinates and UI in CSS pixels, while drawing
 * the WebGL framebuffer at physical-pixel density. Canvas2D keeps its native
 * resolution rather than risking a mismatched transform or touch input.
 */
export function installHighDensityRendering(game: Phaser.Game): void {
  const attach = () => {
    if (game.renderer.type !== Phaser.WEBGL) return;
    const renderer = game.renderer as Phaser.Renderer.WebGL.WebGLRenderer;
    const canvas = game.canvas;
    let frame = 0;
    const restoreLogicalProjection = () => renderer.setProjectionMatrix(game.scale.width, game.scale.height);
    const apply = () => {
      frame = 0;
      const cssWidth = game.scale.width;
      const cssHeight = game.scale.height;
      if (!cssWidth || !cssHeight || renderer.contextLost) return;
      const ratio = renderPixelRatio(window.devicePixelRatio || 1, getPreferences().quality, cssWidth, cssHeight);
      const width = Math.round(cssWidth * ratio);
      const height = Math.round(cssHeight * ratio);
      if (canvas.width !== width || canvas.height !== height || renderer.width !== width || renderer.height !== height) {
        canvas.width = width;
        canvas.height = height;
        renderer.resize(width, height);
      }
      restoreLogicalProjection();
      // Exposed for mobile QA; never change the interactive CSS viewport size.
      document.body.dataset.renderPixelRatio = (width / cssWidth).toFixed(2);
      document.body.dataset.renderBuffer = `${width}x${height}`;
    };
    const schedule = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    };
    // Phaser clips cameras in *logical* coordinates, but WebGL scissor operates in
    // framebuffer pixels. Without this conversion HiDPI draws only one corner.
    const originalPushScissor = renderer.pushScissor.bind(renderer);
    renderer.pushScissor = (x, y, width, height, drawingBufferHeight) => {
      // Off-screen render targets retain their own pixel-space clipping.
      if (renderer.currentFramebuffer) return originalPushScissor(x, y, width, height, drawingBufferHeight);
      const scaleX = renderer.width / game.scale.width;
      const scaleY = renderer.height / game.scale.height;
      return originalPushScissor(
        Math.round(x * scaleX), Math.round(y * scaleY),
        Math.round(width * scaleX), Math.round(height * scaleY),
        drawingBufferHeight,
      );
    };
    renderer.on(Phaser.Renderer.Events.PRE_RENDER, restoreLogicalProjection);
    game.scale.on(Phaser.Scale.Events.RESIZE, schedule);
    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    window.addEventListener('game-preferences-changed', schedule);
    apply();
  };
  if (game.isBooted) attach();
  else game.events.once(Phaser.Core.Events.READY, attach);
}
